// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Alert, Button, Group, Paper, Skeleton, Stack, Text, Title } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import type { ChargeItem, Patient } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import { formatUsd, getPatientPaymentMethod } from '@polarissleep/shared';
import * as Sentry from '@sentry/react';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { botIdentifier } from '../lib/bot-identifier';
import { AddCardModal } from './settings/AddCardModal';

const BRAND = '#640040';
const PAYMENT_CARD_DECLINED_AT_EXTENSION_URL =
  'https://polarissleep.com/fhir/StructureDefinition/payment-card-declined-at';

export function PayChargePage(): JSX.Element {
  const { chargeItemId } = useParams<{ chargeItemId: string }>();
  const navigate = useNavigate();
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Patient;
  const [patient, setPatient] = useState<Patient>(profile);
  const [charge, setCharge] = useState<ChargeItem>();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [addCardOpen, setAddCardOpen] = useState(false);

  useEffect(() => setPatient(profile), [profile]);

  useEffect(() => {
    if (!chargeItemId) {
      return;
    }
    setLoading(true);
    medplum
      .readResource('ChargeItem', chargeItemId)
      .then((nextCharge) => {
        if (
          nextCharge.subject?.reference !== `Patient/${profile.id}` ||
          !['planned', 'billable'].includes(nextCharge.status ?? '')
        ) {
          navigate('/', { replace: true });
          return;
        }
        setCharge(nextCharge);
      })
      .catch((err) => {
        Sentry.captureException(err);
        showNotification({ color: 'red', title: 'Could not load payment', message: 'Please try again.' });
        navigate('/', { replace: true });
      })
      .finally(() => setLoading(false));
  }, [chargeItemId, medplum, navigate, profile.id]);

  async function handleConfirm(): Promise<void> {
    if (!chargeItemId) {
      return;
    }
    setSubmitting(true);
    try {
      await Sentry.startSpan({ op: 'ui.click', name: 'Confirm Patient Charge Payment' }, async () => {
        await medplum.executeBot(botIdentifier('confirm-patient-charge-payment'), { chargeItemId });
      });
      showNotification({ color: 'green', title: 'Payment confirmed', message: 'Your payment was successful.' });
      navigate('/', { replace: true });
    } catch (err) {
      Sentry.captureException(err);
      showNotification({
        color: 'red',
        title: 'Payment failed',
        message: err instanceof Error ? err.message : 'Please try again.',
      });
    } finally {
      setSubmitting(false);
    }
  }

  if (loading || !charge) {
    return <Skeleton height={320} m="md" />;
  }

  const paymentMethod = getPatientPaymentMethod(patient);
  const amount = charge.priceOverride?.value ?? 0;
  const purpose = charge.code?.text ?? charge.code?.coding?.[0]?.display ?? 'Patient charge';
  const cardDeclined = charge.extension?.some((extension) => extension.url === PAYMENT_CARD_DECLINED_AT_EXTENSION_URL);

  return (
    <Stack gap="md" p="md" maw={900} mx="auto" w="100%">
      <Paper p="md" radius="md" withBorder shadow="sm">
        <Title order={1} fz={32} fw={700}>
          Confirm Payment
        </Title>
      </Paper>
      <Paper p="md" radius="md" withBorder shadow="sm">
        <Stack gap="md">
          <Title order={3}>{purpose}</Title>
          {charge.occurrenceDateTime && (
            <Text size="sm">Date of service: {charge.occurrenceDateTime.slice(0, 10)}</Text>
          )}
          <Group justify="space-between">
            <Text>Total Due Today</Text>
            <Title order={3}>{formatUsd(amount)}</Title>
          </Group>
          {paymentMethod ? (
            <Group gap="md">
              <Text>Card ending in {paymentMethod.last4}</Text>
              <Button
                variant="outline"
                style={{ borderColor: BRAND, color: BRAND }}
                onClick={() => setAddCardOpen(true)}
              >
                Edit via Stripe
              </Button>
            </Group>
          ) : (
            <Group justify="space-between" align="center">
              <Text size="sm">No card on file.</Text>
              <Button style={{ backgroundColor: BRAND }} onClick={() => setAddCardOpen(true)}>
                Add card
              </Button>
            </Group>
          )}
          {cardDeclined && (
            <Alert color="red" title="Card declined">
              Your last payment attempt was declined. Update your card before trying again.
            </Alert>
          )}
          <Alert color="blue" variant="light">
            Your saved card will be charged securely by Stripe.
          </Alert>
          <Button
            style={{ backgroundColor: BRAND }}
            loading={submitting}
            disabled={!paymentMethod || submitting}
            onClick={handleConfirm}
          >
            Pay {formatUsd(amount)}
          </Button>
        </Stack>
      </Paper>
      {patient.id && (
        <AddCardModal
          opened={addCardOpen}
          patientId={patient.id}
          onClose={() => setAddCardOpen(false)}
          onSaved={(updated) => {
            setPatient(updated);
            setAddCardOpen(false);
          }}
        />
      )}
    </Stack>
  );
}
